<!-- pages/readme.vue -->
<template>
    <div class="p-6 max-w-4xl mx-auto space-y-6">
        <header class="space-y-1">
            <h1 class="text-3xl font-bold">Client README</h1>
            <p class="text-sm text-gray-500">Automatisch gegenereerd op basis van de project configuratie.</p>
        </header>

        <section v-if="loading" class="text-gray-600">Laden…</section>
        <section v-else-if="error" class="text-red-600">{{ error }}</section>

        <section v-else class="space-y-4">
            <div class="flex gap-3">
                <button @click="downloadMd" class="px-3 py-2 rounded-lg border shadow-sm hover:bg-gray-50 active:shadow">Download readme.md</button>
                <button @click="copyMd" class="px-3 py-2 rounded-lg border shadow-sm hover:bg-gray-50 active:shadow">Kopieer markdown</button>
            </div>

            <!-- Rendered Markdown preview -->
            <article class="prose max-w-none" v-html="renderedMarkdown"></article>

            <!-- Raw markdown (toggle as needed) -->
            <details class="mt-4">
                <summary class="cursor-pointer">Toon ruwe markdown</summary>
                <pre class="mt-2 bg-gray-50 p-3 rounded overflow-auto text-sm">{{ markdown }}</pre>
            </details>
        </section>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'

import { config } from "config";

// Minimalistische markdown renderer (geen extra deps)
function simpleMdToHtml(md) {
    const esc = (s) =>
        s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

    md = md.replace(/```([\s\S]*?)```/g, (_m, code) => `<pre><code>${esc(code)}</code></pre>`)
    md = md.replace(/^###\s+(.+)$/gm, '<h3>$1</h3>')
        .replace(/^##\s+(.+)$/gm, '<h2>$1</h2>')
        .replace(/^#\s+(.+)$/gm, '<h1>$1</h1>')
    md = md.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
    md = md.replace(/^(?:-\s+.+(?:\n|$))+?/gm, (block) => {
        const items = block.trim().split(/\n/).map(li => li.replace(/^[-*]\s+/, ''))
        return `<ul>${items.map(i => `<li>${i}</li>`).join('')}</ul>`
    })
    md = md.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>')
    md = md.replace(/^(?!<h\d|<ul>|<pre>|<hr>)(.+)$/gm, '<p>$1</p>')
    return md
}

const loading = ref(false);
const error = ref(null);

const environmentConfig = ref(process.env.NODE_ENV === 'development' ? {} : environmentJson);
if (process.env.NODE_ENV === 'development') {
    // For development purpose load the config json async so that it renews the localhost environment variables
    fetch(`/env/${process.env.VUE_APP_ENVIRONMENT_CONFIG}.json`).then(response => {
        return response.json();
    }).then(data => {
        console.log("data",data);
        environmentConfig.value = data;
    });
}
const env = computed(() => {
    return environmentConfig.value;
})
console.log("env",env);

const facts = computed(() => {
    const e = env.value || {};
    console.log("E",e);
    const f = {
        clientNaam: e.name || e.slug || 'client',
        versie: e.version || 'onbekend',
        meEndpoint: (e.auth && e.auth.meEndpoint) || findFallbackMe(e) || 'onbekend',
        apiBaseUrl: e.apiBaseUrl || 'onbekend',
        tokenIn: (e.auth && e.auth.tokenLocation) || 'header',
        tokenHeader: (e.auth && e.auth.tokenHeaderName) || 'Authorization',
        company: {
            verplicht: e.entities && e.entities.company && typeof e.entities.company.required !== 'undefined'
                ? e.entities.company.required
                : inferCompanyRequired(e),
            meerdereToegestaan: e.entities && e.entities.company && typeof e.entities.company.multipleAllowed !== 'undefined'
                ? e.entities.company.multipleAllowed
                : inferCompanyMultiple(e),
            relationKey: (e.entities && e.entities.company && e.entities.company.relationKey)
                || inferCompanyRelationKey(e)
                || 'companyId',
            label: (e.entities && e.entities.company && e.entities.company.label) || 'Bedrijf',
        },
        relaties: describeRelations(e),
        featuresAan: listTrueFlags(e.features),
        regels: flattenRules(e.rules),
    }
    return f
})

function listTrueFlags(flags) {
    if (!flags) return []
    return Object.entries(flags).filter(([, v]) => v === true).map(([k]) => k)
}

function flattenRules(r) {
    if (!r) return []
    const out = []
    const walk = (obj, path = []) => {
        Object.entries(obj).forEach(([k, v]) => {
            const p = [...path, k]
            if (v && typeof v === 'object' && !Array.isArray(v)) {
                walk(v, p)
            } else {
                out.push(`${p.join('.')} = ${JSON.stringify(v)}`)
            }
        })
    }
    walk(r)
    return out
}

function describeRelations(e) {
    const rels = []
    const user = (e.entities && e.entities.user) || {}
    const contact = (e.entities && e.entities.contact) || {}

    const hasUserContact = hasAnyKey(user, ['contactId', 'contact', 'contact_ids', 'contactIds'])
    if (hasUserContact) {
        rels.push('Een gebruiker is gekoppeld aan een contact (User → Contact).')
    } else if (Object.keys(contact).length) {
        rels.push('Contacten bestaan, maar er is geen expliciete sleutel op User gevonden.')
    }

    const company = (e.entities && e.entities.company) || {}
    const contactCompany = hasAnyKey(contact, ['companyId', 'company', 'company_ids', 'companyIds'])
    if (contactCompany) {
        rels.push('Een contact is gekoppeld aan een bedrijf (Contact → Company).')
    } else if (Object.keys(company).length) {
        rels.push('Bedrijven bestaan, maar er is geen expliciete sleutel op Contact gevonden.')
    }
    return rels
}

function hasAnyKey(obj, keys) {
    const lower = Object.keys(obj).map(k => k.toLowerCase())
    return keys.some(k => lower.includes(k.toLowerCase()))
}

function inferCompanyRequired(e) {
    const user = (e.entities && e.entities.user) || {}
    const contact = (e.entities && e.entities.contact) || {}
    const requiredHints = ['companyId', 'company']
    const maybe = hasAnyKey(user, requiredHints) || hasAnyKey(contact, requiredHints)
    return !!maybe
}

function inferCompanyMultiple(e) {
    const user = (e.entities && e.entities.user) || {}
    const contact = (e.entities && e.entities.contact) || {}
    const multiHints = ['companyIds', 'company_ids']
    return hasAnyKey(user, multiHints) || hasAnyKey(contact, multiHints)
}

function inferCompanyRelationKey(e) {
    const candidates = ['companyIds', 'company_ids', 'companyId', 'company']
    const fromUser = Object.keys((e.entities && e.entities.user) || {}).find(k => candidates.includes(k))
    const fromContact = Object.keys((e.entities && e.entities.contact) || {}).find(k => candidates.includes(k))
    return fromUser || fromContact || null
}

function findFallbackMe(e) {
    const guesses = ['/api/me', '/me', '/users/me']
    return guesses.find(Boolean)
}

const markdown = computed(() => {
    const f = facts.value
    const lines = []
    lines.push(`# README – ${f.clientNaam}`)
    lines.push('')
    lines.push(`Versie: **${f.versie}**`)
    lines.push('')
    lines.push('## API & Authenticatie')
    lines.push(`- Basis-URL: \`${f.apiBaseUrl}\``)
    lines.push(`- /me-eindpunt: \`${f.meEndpoint}\``)
    lines.push(`- Tokenlocatie: \`${f.tokenIn}\``)
    lines.push(`- Headernaam: \`${f.tokenHeader}\``)
    lines.push('')
    lines.push('## Relatiemodel')
    if (f.relaties.length) {
        f.relaties.forEach(r => lines.push(`- ${r}`))
    } else {
        lines.push('- Geen expliciete relaties gevonden in de configuratie.')
    }
    lines.push('')
    lines.push('## Bedrijfsregels (Company)')
    lines.push(`- Bedrijf verplicht: **${f.company.verplicht ? 'ja' : 'nee'}**`)
    lines.push(`- Meerdere bedrijven toegestaan: **${f.company.meerdereToegestaan ? 'ja' : 'nee'}**`)
    lines.push(`- Relation key: \`${f.company.relationKey}\``)
    lines.push(`- Label: \`${f.company.label}\``)
    lines.push('')
    if (f.featuresAan.length) {
        lines.push('## Ingeschakelde features')
        f.featuresAan.forEach(feat => lines.push(`- ${feat}`))
        lines.push('')
    }
    if (f.regels.length) {
        lines.push('## Specifieke regels')
        f.regels.forEach(rule => lines.push(`- ${rule}`))
        lines.push('')
    }
    lines.push('---')
    lines.push('### Tips')
    lines.push('- Pas de heuristieken aan in deze pagina als je custom sleutel-namen gebruikt (zie functies `infer*`).')
    lines.push('- Voeg extra secties toe wanneer nieuwe velden in `env` worden geïntroduceerd.')
    return lines.join('\n')
})

const renderedMarkdown = computed(() => simpleMdToHtml(markdown.value))

async function copyMd() {
    try {
        await navigator.clipboard.writeText(markdown.value)
    } catch {
        // ignore
    }
}

function downloadMd() {
    const blob = new Blob([markdown.value], { type: 'text/markdown;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'readme.md'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
}
</script>

<style scoped>
.prose h1 { @apply text-3xl font-bold mt-4; }
.prose h2 { @apply text-2xl font-semibold mt-6; }
.prose h3 { @apply text-xl font-semibold mt-4; }
.prose p { @apply leading-relaxed; }
.prose ul { @apply list-disc pl-6; }
pre { @apply rounded; }
</style>
